// ArticleForm.js - Create/Edit Knowledge Base Article

import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, MenuItem, FormControl, InputLabel, Select,
  CircularProgress, Alert, Chip, Box, Typography
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';
import { Color } from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import TiptapEditor from '../../../../components/Common/TiptapEditor';
import { articleService } from './helpDeskService';
import { ArticleStatusOptions, ArticleVisibilityOptions } from './helpDeskEnums';

const ArticleForm = ({ open, onClose, article, chatbotId, categories = [] }) => {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = Boolean(article);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    summary: '',
    content: '',
    category_id: '',
    visibility: 'INTERNAL',
    status: 'DRAFT',
    tags: [],
    meta_title: '',
    meta_description: ''
  });

  const [tagInput, setTagInput] = useState('');

  // TipTap editor for Summary
  const summaryEditor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: formData.summary,
    onUpdate: ({ editor }) => {
      setFormData(prev => ({ ...prev, summary: editor.getHTML() }));
    },
  });

  // TipTap editor for Content
  const contentEditor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: formData.content,
    onUpdate: ({ editor }) => {
      setFormData(prev => ({ ...prev, content: editor.getHTML() }));
    },
  });

  useEffect(() => {
    if (open) {
      if (article) {
        setFormData({
          title: article.title || '',
          summary: article.summary || '',
          content: article.content || '',
          category_id: article.category_id || '',
          visibility: article.visibility || 'INTERNAL',
          status: article.status || 'DRAFT',
          tags: article.tags || [],
          meta_title: article.meta_title || '',
          meta_description: article.meta_description || ''
        });
        // Update editors with article content
        if (summaryEditor) {
          summaryEditor.commands.setContent(article.summary || '');
        }
        if (contentEditor) {
          contentEditor.commands.setContent(article.content || '');
        }
      } else {
        setFormData({
          title: '',
          summary: '',
          content: '',
          category_id: '',
          visibility: 'INTERNAL',
          status: 'DRAFT',
          tags: [],
          meta_title: '',
          meta_description: ''
        });
        // Clear editors
        if (summaryEditor) {
          summaryEditor.commands.setContent('');
        }
        if (contentEditor) {
          contentEditor.commands.setContent('');
        }
      }
      setError(null);
    }
  }, [open, article, summaryEditor, contentEditor]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }

    // Get content from editor
    const content = contentEditor ? contentEditor.getHTML() : formData.content;
    if (!content || content === '<p></p>') {
      setError('Content is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = {
        ...formData,
        summary: summaryEditor ? summaryEditor.getHTML() : formData.summary,
        content: content,
        chatbot_id: chatbotId,
        category_id: formData.category_id || null
      };

      let response;
      if (isEdit) {
        response = await articleService.update(article.id, data);
      } else {
        response = await articleService.create(data);
      }

      if (response.success) {
        enqueueSnackbar(
          isEdit ? 'Article updated successfully' : 'Article created successfully',
          { variant: 'success' }
        );
        onClose(true);
      } else {
        setError(response.message || 'Failed to save article');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save article');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="lg" fullWidth>
      <DialogTitle>
        {isEdit ? 'Edit Article' : 'Create New Article'}
      </DialogTitle>
      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Title"
              required
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              placeholder="Article title"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
              Summary
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              A short summary that appears in search results
            </Typography>
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
              <TiptapEditor editor={summaryEditor} height="100px" />
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
              Content <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Write your article content with rich text formatting
            </Typography>
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
              <TiptapEditor editor={contentEditor} height="300px" />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category_id}
                label="Category"
                onChange={(e) => handleChange('category_id', e.target.value)}
              >
                <MenuItem value="">No Category</MenuItem>
                {categories.map(cat => (
                  <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Visibility</InputLabel>
              <Select
                value={formData.visibility}
                label="Visibility"
                onChange={(e) => handleChange('visibility', e.target.value)}
              >
                {ArticleVisibilityOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Box>
                      <Typography variant="body2">{opt.label}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {opt.description}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={(e) => handleChange('status', e.target.value)}
              >
                {ArticleStatusOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box>
              <TextField
                fullWidth
                label="Add Tags"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
                placeholder="Press Enter to add tag"
                size="small"
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {formData.tags.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onDelete={() => handleRemoveTag(tag)}
                  />
                ))}
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
              SEO Settings (Optional)
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Meta Title"
              value={formData.meta_title}
              onChange={(e) => handleChange('meta_title', e.target.value)}
              placeholder="SEO title for search engines"
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Meta Description"
              value={formData.meta_description}
              onChange={(e) => handleChange('meta_description', e.target.value)}
              placeholder="SEO description for search engines"
              size="small"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={() => onClose(false)} disabled={loading} size="small">
          Cancel
        </Button>
        {!isEdit && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              handleChange('status', 'DRAFT');
              handleSubmit();
            }}
            disabled={loading}
          >
            Save as Draft
          </Button>
        )}
        <Button
          variant="contained"
          size="small"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading && <CircularProgress size={20} />}
        >
          {isEdit ? 'Update Article' : 'Publish Article'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArticleForm;
